import objectFitImages from 'object-fit-images';
import ImageBuddy from 'imagebuddy';

import ThemeWelcome from './components/testing/theme-welcome';
import SiteHeader from './components/site-header';
import BannerVideo from './components/banner-video';
import Modal from './components/modal';

import PostsListBlog from './components/posts-list/posts-list-blog';
import PostsListSearch from './components/posts-list/posts-list-search';

import { processExternalLinks } from './util/general-util';
import ScrollWatcher from './util/scroll-watcher';
import SiteEvents, { SiteEventNames } from './util/site-events';
import { loadIntersectionObserver } from './util/load-dependencies';
import { createSingleUseObserver } from './util/intersection-observer';
import { initHelloBar } from './util/hello-bar';
import ModalVideoPopup from './components/modal-video-popup';

import CaseStudiesSlider from './components/case-studies-slider';
import LogosSlider from './components/logos-silder';
import MediaContentSlider from './components/media-content-slider';
import TestimonialsSlider from './components/testimonials-slider';
import StatsSlider from './components/stats-slider';
import SocialShare from './components/social-share';
import Accordion from './components/accordion';
import SideTabs from './components/side-tabs';
import NewsletterSubmit from './components/newsletter-submit';
import AnchorLinks from './components/anchor-links';

import AccordionImage from './blocks/accordion-image';
import PostsSlider from './blocks/posts-slider';

import ResourceTemplate from './templates/resource';
import EventTemplate from './templates/event';
import ImageSlider from './blocks/image-slider';
import ProductModal from "./components/product-modal";

class App {
	constructor() {
		this.instances = {
			components: {
				accordion: [],
				anchorLinks: null,
				bannerVideo: null,
				caseStudiesSlider: [],
				logosSlider: [],
				mediaContentSlider: [],
				modalVideoPopup: [],
				newsletterSubmit: [],
				postsListBlog: [],
				postsListSearch: [],
				siteHeader: null,
				socialShare: null,
				statsSlider: [],
				testimonialsSlider: [],
				themeWelcome: null
			},
			templates: {
				resourceTemplate: null,
				eventTemplate: null
			},
			blocks: {
				accordionImage: [],
				sideTabs: [],
				postsSlider: [],
				imageSlider: []
			},
			scrollWatcher: null,
			imageBuddy: null
		};

		this.init();
		this.initComponents();
		this.initTemplates();
		this.initBlocks();
		this.initSocialShare();
	}

	init() {
		processExternalLinks({
			target: '_blank',
			rel: 'noopener'
		});

		Modal.setDefaults({
			closeDuration: 400,
			closeButtonContent: `
				<svg xmlns="http://www.w3.org/2000/svg" class="modal__close-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
					<line x1="18" y1="6" x2="6" y2="18"></line>
					<line x1="6" y1="6" x2="18" y2="18"></line>
				</svg>
			`.trim()
		});

		this.scrollWatcher = new ScrollWatcher((params) => {
			ScrollWatcher.defaultCallback(params, 100);
		});

		// initialize ImageBuddy and setup events
		this.instances.imageBuddy = new ImageBuddy({
			lazyLoad: true
			// debug: true
		});

		ImageBuddy.on('image-loaded', (imgEl) => {
			objectFitImages(imgEl);
		});

		SiteEvents.subscribe(SiteEventNames.IMAGEBUDDY_TRIGGER_UPDATE, (opts) => {
			this.instances.imageBuddy.update(opts || {});
		});

		initHelloBar((hbEl) => {
			document.body.style.marginTop = `${hbEl.offsetHeight}px`;

			const siteHeaderEl = document.querySelector('.site-header');

			if (siteHeaderEl) {
				siteHeaderEl.style.marginTop = `${hbEl.offsetHeight}px`;
			}
		});

		// ObjectFitImages: only act upon images that have a valid src attribute
		// Note: use the 'object-fit-polyfill' SCSS mixin to create the necessary 'font-family' attribute
		objectFitImages('img:not([src^="data:"])');
	}

	async initComponents() {
		// theme welcome
		// TODO: remove during development
		const themeWelcomeEl = document.querySelector('.theme-welcome');

		if (themeWelcomeEl) {
			this.instances.themeWelcome = new ThemeWelcome(themeWelcomeEl);
		}

		// site header
		const siteHeaderEl = document.querySelector('.site-header');

		if (siteHeaderEl) {
			this.instances.components.siteHeader = new SiteHeader(siteHeaderEl);
		}

		// newsletter signup
		Array.from(document.querySelectorAll('.newsletter-signup')).forEach(
			(newsletterSignupEl) => {
				this.instances.components.newsletterSubmit.push(
					new NewsletterSubmit(newsletterSignupEl)
				);
			}
		);

		// banner cover video
		const bannerCoverVideoEl = document.querySelector('.banner__cover-video');

		if (bannerCoverVideoEl) {
			this.instances.components.bannerVideo = new BannerVideo(bannerCoverVideoEl);
		}

		// posts list - blog
		Array.from(document.querySelectorAll('.posts-list--blog')).forEach((postsListBlogEl) => {
			this.instances.components.postsListBlog.push(new PostsListBlog(postsListBlogEl));
		});

		// posts list - search
		Array.from(document.querySelectorAll('.posts-list--search')).forEach(
			(postsListSearchEl) => {
				this.instances.components.postsListSearch.push(
					new PostsListSearch(postsListSearchEl)
				);
			}
		);

		// Modal Video Popup
		Array.from(document.querySelectorAll('.js__modal--video')).forEach((modalVideoPopupEl) => {
			this.instances.components.modalVideoPopup.push(new ModalVideoPopup(modalVideoPopupEl));
		});

		// Testimonials Slider
		Array.from(document.querySelectorAll('.block-testimonials')).forEach(
			(testimonialsBlock) => {
				this.slider = testimonialsBlock.querySelector(
					'.block-testimonials__testimonials-slider'
				);
				this.prevButton = testimonialsBlock.querySelector('.custom-prev-button');
				this.nextButton = testimonialsBlock.querySelector('.custom-next-button');
				this.instances.components.testimonialsSlider.push(
					new TestimonialsSlider(this.slider, this.prevButton, this.nextButton)
				);
			}
		);

		// Case Studies Slider
		Array.from(document.querySelectorAll('.block-case-studies')).forEach((caseStudiesBlock) => {
			this.slider = caseStudiesBlock.querySelector(
				'.block-case-studies__case-studies-slider'
			);
			this.prevButton = caseStudiesBlock.querySelector('.custom-prev-button');
			this.nextButton = caseStudiesBlock.querySelector('.custom-next-button');
			this.nav = caseStudiesBlock.querySelector('.custom-nav-container-case-studies');

			this.instances.components.caseStudiesSlider.push(
				new CaseStudiesSlider(this.slider, this.prevButton, this.nextButton, this.nav)
			);
		});

		// Logos Slider
		Array.from(document.querySelectorAll('.block-logos')).forEach((logosBlock) => {
			this.slider = logosBlock.querySelector('.block-logos__logos-slider');
			this.instances.components.logosSlider.push(new LogosSlider(this.slider));
		});

		// Media Content Slider
		Array.from(document.querySelectorAll('.block-media-content__slider-wrapper')).forEach(
			(mediaContentSlider) => {
				this.slider = mediaContentSlider.querySelector('.block-media-content__slider');
				this.instances.components.mediaContentSlider.push(
					new MediaContentSlider(this.slider)
				);
			}
		);

		// Social Share
		const socialShareEl = document.querySelector('.tmpl-resource-single');

		if (socialShareEl) {
			this.instances.components.socialShare = new SocialShare(socialShareEl);
		}

		// accordion
		Array.from(document.querySelectorAll('.accordion-container')).forEach((accordionEl) => {
			this.instances.components.accordion.push(new Accordion(accordionEl));
		});

		// Stats Slider
		Array.from(document.querySelectorAll('.block-stats')).forEach((statsBlock) => {
			this.slider = statsBlock.querySelector('.stats__stat-slider');
			this.nav = statsBlock.querySelector('.custom-nav-container-stats');
			this.instances.components.statsSlider.push(new StatsSlider(this.slider, this.nav));
		});

		// Anchor Links
		const anchorLinksEl = document.querySelector('.banner__anchor-links-wrapper');

		if (anchorLinksEl) {
			this.instances.components.anchorLinks = new AnchorLinks(anchorLinksEl);
		}

		const productModal = new ProductModal();
		productModal.init();
	}

	initTemplates() {
		// banner cover video
		const resourceTemplateEl = document.querySelector('.post-type-archive-resource');

		if (resourceTemplateEl) {
			this.instances.templates.resourceTemplate = new ResourceTemplate(resourceTemplateEl);
		}

		const eventTemplateEl = document.querySelector('.post-type-archive-event');

		if (eventTemplateEl) {
			this.instances.templates.eventTemplate = new EventTemplate(eventTemplateEl);
		}
	}

	async initBlocks() {
		// Accordion Image
		Array.from(document.querySelectorAll('.block-accordion-image')).forEach(
			(accordionImage) => {
				this.instances.blocks.accordionImage.push(new AccordionImage(accordionImage));
			}
		);

		// Side Tabs
		Array.from(document.querySelectorAll('.block-side-tabs')).forEach((sideTabs) => {
			this.instances.blocks.sideTabs.push(new SideTabs(sideTabs));
		});

		// Posts Slider
		Array.from(document.querySelectorAll('.block-posts-slider')).forEach((postsSliderEl) => {
			this.instances.blocks.postsSlider.push(new PostsSlider(postsSliderEl));
		});

		// Image Slider
		Array.from(document.querySelectorAll('.block-image-slider')).forEach((imageSliderEl) => {
			this.instances.blocks.imageSlider.push(new ImageSlider(imageSliderEl));
		});

		// setup block animation watchers
		const animateBlocks = document.querySelectorAll('.block-animate');

		if (animateBlocks && animateBlocks.length) {
			try {
				await loadIntersectionObserver();

				animateBlocks.forEach((watchEl) =>
					createSingleUseObserver(watchEl, (entry, el) => {
						el.classList.remove('block-animate');
					})
				);
			} catch (err) {
				console.error(err);
			}
		}
	}

	initSocialShare() {
		const socialShareEls = document.querySelectorAll('[data-social-share]');

		Array.from(socialShareEls).forEach((el) => {
			el.addEventListener('click', (e) => {
				const site = el.getAttribute('data-social-share');
				const shareUrl = el.getAttribute('href');

				e.preventDefault();

				if (!shareUrl) {
					return;
				}

				window.open(shareUrl, `${site}Share`, 'width=626,height=436');
			});
		});
	}
}

export default App;
